import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
} from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { analytics } from "@/services/analytics";
import { useSessionTimeout } from "@/hooks/useSessionTimeout";
import { IDLE_SESSION_TIMEOUT_MS, IDLE_SESSION_WARNING_MS } from "@/constants/sessionPolicy";
import { API_URL } from "@/config/environment";
import { markJustSignedOut } from "@/utils/postLoginRedirect";
import { authService } from "@/services/auth";
import { signInWithPasswordTimed } from "@/utils/supabasePasswordSignIn";
import { isTimeoutError } from "@/utils/promiseWithTimeout";
import { devLog } from "@/utils/devLog";
import { setUser as setSentryUser } from "@/services/sentry";
import { AUTH_BOOTSTRAP_LOADING_MS, AUTH_SESSION_RESTORE_MS } from "@/utils/authBootstrapPolicy";
import {
  finalizePasswordSignIn,
  handleFailedPasswordSignIn,
  processAuthStateChangeEvent,
  validateSignInLockout,
  warnIfApproachingLockout,
} from "@/context/authContextHelpers";
import {
  handleGetSessionCatch,
  handleInitialGetSessionResult,
  recoverSessionAfterRestoreTimeout,
  scheduleAuthBootstrapTimers,
} from "@/context/authContextBootstrapHelpers";

export interface SignInOptions {
  onLockoutWarning?: (message: string) => void;
}

export interface SignInResult {
  error: Error | null;
  mfaRequired?: boolean;
  accountLocked?: boolean;
  lockoutCheckUnavailable?: boolean;
  failedAttemptsMessage?: string | null;
  accessToken?: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (
    email: string,
    password: string,
    captchaToken?: string,
    options?: SignInOptions
  ) => Promise<SignInResult>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string, captchaToken?: string) => Promise<{ error: Error | null }>;
  signInWithOAuth: (provider: "google" | "azure") => Promise<{ error: Error | null }>;
  mfaChallenge: boolean;
  mfaFactorId: string | null;
  mfaChallengeId: string | null;
  verifyMFA: (code: string) => Promise<{ error: Error | null }>;
  clearMFAChallenge: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

/**
 * Auth Provider.
 */
export function AuthProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaChallenge, setMfaChallenge] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [mfaChallengeId, setMfaChallengeId] = useState<string | null>(null);
  const manualPasswordSignInRef = useRef(false);

  // Session timeout handler - wrapped in useCallback to prevent timeout reset on re-renders
  const handleSessionTimeout = useCallback(async () => {
    devLog.warn("🔒 Session timeout detected after 60 minutes of inactivity, signing out...");
    // Track as logout since session timeout results in logout
    analytics.trackAuthEvent("logout", { email: user?.email, reason: "session_timeout" });
    await supabase.auth.signOut({ scope: "local" });
  }, [user?.email]);

  useSessionTimeout({
    onTimeout: handleSessionTimeout,
    timeoutDuration: IDLE_SESSION_TIMEOUT_MS,
    warningDuration: IDLE_SESSION_WARNING_MS,
    isActive: !!user && !loading, // Only active when user is logged in and not loading
  });

  // Simple MFA state setters
  const setMfaChallengeSimple = (value: boolean) => {
    devLog.warn(`🔧 MFA Challenge: ${value}`);
    setMfaChallenge(value);
  };

  const setMfaFactorIdSimple = (value: string | null) => {
    devLog.warn(`🔧 MFA Factor ID: ${value}`);
    setMfaFactorId(value);
  };

  const setMfaChallengeIdSimple = (value: string | null) => {
    devLog.warn(`🔧 MFA Challenge ID: ${value}`);
    setMfaChallengeId(value);
  };

  // Clear MFA challenge
  const clearMFAChallenge = useCallback(() => {
    devLog.warn("🔧 Clearing MFA challenge");
    setMfaChallengeSimple(false);
    setMfaFactorIdSimple(null);
    setMfaChallengeIdSimple(null);
  }, []);

  // MFA verification
  const verifyMFA = useCallback(
    async (code: string) => {
      if (!mfaFactorId || !mfaChallengeId) {
        return { error: new Error("MFA challenge not found") };
      }

      try {
        devLog.warn("🔐 Verifying MFA code...");
        const { error } = await supabase.auth.mfa.verify({
          factorId: mfaFactorId,
          challengeId: mfaChallengeId,
          code,
        });

        if (error) {
          devLog.warn("❌ MFA verification failed:", error);
          return { error };
        }

        devLog.warn("✅ MFA verification successful");
        clearMFAChallenge();
        return { error: null };
      } catch (error) {
        devLog.warn("❌ MFA verification exception:", error);
        return { error };
      }
    },
    [mfaFactorId, mfaChallengeId, clearMFAChallenge]
  );

  // Simple MFA state tracking
  useEffect(() => {
    if (mfaChallenge) {
      devLog.warn("🔒 MFA CHALLENGE ACTIVE");
    } else {
      devLog.warn("🔓 MFA CHALLENGE INACTIVE");
    }
  }, [mfaChallenge]);

  useEffect(() => {
    setSentryUser(
      user
        ? {
            id: user.id,
            email: user.email ?? undefined,
          }
        : null
    );
  }, [user]);

  useEffect(() => {
    devLog.warn("🔧 AuthContext: Initializing authentication");

    let mounted = true;
    let mfaCheckInProgress = false;
    let sessionRestorePending = false;

    const bootstrapActions = {
      setSession,
      setUser,
      setLoading,
      setSessionRestorePending: (value: boolean) => {
        sessionRestorePending = value;
      },
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      processAuthStateChangeEvent(
        event,
        newSession,
        {
          mounted,
          user,
          session,
          loading,
          mfaChallenge,
          mfaFactorId,
          mfaChallengeId,
          mfaCheckInProgress,
          manualPasswordSignIn: manualPasswordSignInRef.current,
          sessionRestorePending,
        },
        {
          setSession,
          setUser,
          setLoading,
          setMfaChallenge: setMfaChallengeSimple,
          setMfaFactorId: setMfaFactorIdSimple,
          setMfaChallengeId: setMfaChallengeIdSimple,
          setMfaCheckInProgress: (value) => {
            mfaCheckInProgress = value;
          },
          setSessionRestorePending: (value) => {
            sessionRestorePending = value;
          },
        },
        () => mounted
      );
    });

    const { authBootstrapTimeout, sessionRestoreFailSafe } = scheduleAuthBootstrapTimers({
      mountedRef: () => mounted,
      getSessionRestorePending: () => sessionRestorePending,
      setLoading,
      onSessionRestoreTimeout: () => {
        void recoverSessionAfterRestoreTimeout(mounted, bootstrapActions);
      },
      bootstrapMs: AUTH_BOOTSTRAP_LOADING_MS,
      sessionRestoreMs: AUTH_SESSION_RESTORE_MS,
    });

    supabase.auth
      .getSession()
      .then(({ data: { session: initialSession }, error }) => {
        handleInitialGetSessionResult(initialSession, error, mounted, bootstrapActions);
      })
      .catch((error) => {
        handleGetSessionCatch(error, mounted, setLoading);
      });

    return () => {
      mounted = false;
      clearTimeout(authBootstrapTimeout);
      clearTimeout(sessionRestoreFailSafe);
      subscription.unsubscribe();
    };
    // Dependencies intentionally omitted - this effect should only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = useCallback(
    async (
      email: string,
      password: string,
      captchaToken?: string,
      options?: SignInOptions
    ): Promise<SignInResult> => {
      manualPasswordSignInRef.current = true;
      setLoading(true);

      try {
        devLog.warn("🔐 Signing in via pure Supabase...");

        const normalizedEmail = email.trim().toLowerCase();
        const lockoutBlock = await validateSignInLockout(normalizedEmail);
        if (lockoutBlock) {
          return lockoutBlock;
        }

        const preSignInLockout = await authService.checkAccountLockout(normalizedEmail);
        warnIfApproachingLockout(preSignInLockout, options?.onLockoutWarning);

        const signInParams: {
          email: string;
          password: string;
          options?: { captchaToken?: string };
        } = {
          email,
          password,
        };

        if (captchaToken) {
          signInParams.options = {
            captchaToken: captchaToken,
          };
        }

        const { data, error } = await signInWithPasswordTimed(signInParams);

        if (error) {
          devLog.warn("❌ Sign in error:", error);
          return handleFailedPasswordSignIn(normalizedEmail, error);
        }

        await authService.logLoginAttempt({
          email: normalizedEmail,
          success: true,
          userId: data.user?.id ?? null,
        });

        const accessToken = data.session?.access_token ?? null;

        if (!data.session) {
          return { error: new Error("No session returned from sign-in.") };
        }

        devLog.warn("✅ Authentication successful via Supabase");

        return finalizePasswordSignIn(data.session, email, accessToken, {
          setUser,
          setSession,
          setLoading,
          setMfaChallenge,
          setMfaFactorId,
          setMfaChallengeId,
        });
      } catch (error) {
        devLog.warn("❌ Sign in exception:", error);
        if (isTimeoutError(error)) {
          return { error: error instanceof Error ? error : new Error(String(error)) };
        }
        return { error: error instanceof Error ? error : new Error(String(error)) };
      } finally {
        manualPasswordSignInRef.current = false;
        setLoading(false);
      }
    },
    []
  );

  const signUp = useCallback(async (email: string, password: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) {
        setLoading(false);
        return { error };
      }

      if (data.user) {
        analytics.trackAuthEvent("signup", { email });
      }

      setLoading(false);
      return { error: null };
    } catch (error) {
      setLoading(false);
      return { error };
    }
  }, []);

  const signOut = useCallback(async () => {
    setLoading(true);
    try {
      const currentPath = globalThis.location.pathname;
      markJustSignedOut();
      await supabase.auth.signOut();
      clearMFAChallenge();
      analytics.trackAuthEvent("logout", { email: user?.email });

      // If logging out from a public route (like /blog), stay there
      // Otherwise, the ProtectedLayout will handle redirect
      const isOnPublicRoute =
        currentPath === "/" ||
        currentPath === "/blog" ||
        currentPath.startsWith("/blog/") ||
        currentPath === "/about" ||
        currentPath === "/pricing" ||
        currentPath === "/contact";

      if (!isOnPublicRoute) {
        // Not on public route - ProtectedLayout will redirect to /
        // Don't do anything here, let ProtectedLayout handle it
      }
    } catch (error) {
      console.error("Sign out error:", error);
    } finally {
      setLoading(false);
    }
  }, [user?.email, clearMFAChallenge]);

  const resetPassword = useCallback(async (email: string, captchaToken?: string) => {
    setLoading(true);
    try {
      // Must go through auth-service so captcha_token reaches Supabase Auth.
      const result = await authService.resetPassword(email, captchaToken);
      setLoading(false);
      return { error: result.error ?? null };
    } catch (error) {
      setLoading(false);
      return { error: error as Error };
    }
  }, []);

  /**
   * Sign in with OAuth provider (Google or Azure)
   * Iteration 2: OAuth sign-in implementation
   */
  const signInWithOAuth = useCallback(async (provider: "google" | "azure") => {
    try {
      devLog.warn(`🔵 OAuth: Initiating ${provider} sign-in...`);
      setLoading(true);

      // Use production domain for redirect URL to show user-friendly domain in OAuth consent screen
      // In production, use API_URL (tellerwand.com), in development use current origin
      const isProduction = import.meta.env.PROD;
      const redirectUrl = isProduction ? `${API_URL}/auth` : `${globalThis.location.origin}/auth`;

      devLog.warn(`🔵 OAuth: Redirect URL: ${redirectUrl}`);

      // Map provider names to Supabase provider IDs
      const providerId = provider === "azure" ? "azure" : "google";

      // Initiate OAuth flow
      const { error } = await supabase.auth.signInWithOAuth({
        provider: providerId,
        options: {
          redirectTo: redirectUrl,
          // Skip browser redirect for now - we'll handle it manually
          // This allows us to show loading state
        },
      });

      if (error) {
        console.error("❌ OAuth provider error:", provider, error);
        setLoading(false);
        return { error };
      }

      // OAuth will redirect the browser to the provider
      // The redirect will come back to /auth with hash fragments
      // AuthContext will handle the callback via onAuthStateChange
      devLog.warn(`✅ OAuth ${provider} redirect initiated`);

      // Don't set loading to false here - the redirect will happen
      // The loading state will be managed by the auth state change handler
      // OAuthButtons component will handle clearing its own loading state if user navigates back

      return { error: null };
    } catch (error) {
      console.error("❌ OAuth provider exception:", provider, error);
      setLoading(false);
      return { error };
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      session,
      loading,
      signIn,
      signUp,
      signOut,
      resetPassword,
      signInWithOAuth,
      mfaChallenge,
      mfaFactorId,
      mfaChallengeId,
      verifyMFA,
      clearMFAChallenge,
    }),
    [
      user,
      session,
      loading,
      mfaChallenge,
      mfaFactorId,
      mfaChallengeId,
      signIn,
      signUp,
      signOut,
      resetPassword,
      signInWithOAuth,
      verifyMFA,
      clearMFAChallenge,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
